import { PersonAddOutlined } from '@mui/icons-material';
import { Button, Stack } from '@mui/material';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import PageHeader from 'components/v2/common/pageHeader';
import ConsultantServiceProvidersDialog from 'views/v2/adminPanel/components/consultantServiceProvidersDialog';
import ConsultantsFilters from 'views/v2/adminPanel/components/consultantsFilters';
import ConsultantsTable from 'views/v2/adminPanel/components/consultantsTable';
import CreateConsultantDialog from 'views/v2/adminPanel/components/createConsultantDialog';
import EditConsultantDialog from 'views/v2/adminPanel/components/editConsultantDialog';

import api from 'api';
import { ACTIONS } from 'constant';
import useModalObj from 'hooks/useModalObj';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import type {
    AdminConsultant,
    AdminConsultantRole,
    ConsultantServiceProviderChange,
    CreateConsultantFormValues,
    CreateConsultantPayload,
    EditConsultantFormValues,
    ServiceProviderAdminRow,
} from 'types';
import { getErrorMessage } from 'utils/v2/errorHelper';
import {
    ADMIN_CONSULTANT_ROLES,
    getAdminConsultantName,
    getConsultantLicenseEdits,
    getConsultantProfileUpdate,
    persistConsultantLicenseEdits,
    toAdminConsultant,
} from 'views/v2/adminPanel/helpers/adminConsultants';

interface ConsultantsPanelProps {
    headerControls?: ReactNode;
}

const CONSULTANTS_PAGE_SIZE = 100;

const loadAllConsultants = async (): Promise<AdminConsultant[]> => {
    const firstResponse = await api.admin.getConsultants(1, CONSULTANTS_PAGE_SIZE);
    const totalPages = firstResponse.data.pagination.TotalPages;
    if (totalPages <= 1) return firstResponse.data.consultants;

    const remainingResponses = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, index) =>
            api.admin.getConsultants(index + 2, CONSULTANTS_PAGE_SIZE)
        )
    );

    return [
        ...firstResponse.data.consultants,
        ...remainingResponses.flatMap((response) => response.data.consultants),
    ];
};

export default function ConsultantsPanel({ headerControls }: ConsultantsPanelProps) {
    const dispatch = useDispatch();
    const { translate } = useTranslate();
    const [consultants, setConsultants] = useState<AdminConsultant[]>([]);
    const [providers, setProviders] = useState<ServiceProviderAdminRow[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selectedRole, setSelectedRole] = useState<AdminConsultantRole | ''>('');
    const [selectedProviderId, setSelectedProviderId] = useState<number | null>(null);
    const { modalObject, setModalObject, onResetModalObj } = useModalObj<AdminConsultant>();

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setIsLoading(true);
            try {
                const [consultantsResponse, providersResponse] = await Promise.all([
                    loadAllConsultants(),
                    api.serviceProvider.list(),
                ]);
                if (cancelled) return;

                setConsultants(
                    consultantsResponse.filter((consultant) =>
                        ADMIN_CONSULTANT_ROLES.includes(consultant.role)
                    )
                );
                setProviders(
                    [...providersResponse.data].sort((left, right) =>
                        left.company_name.localeCompare(right.company_name)
                    )
                );
            } catch (error) {
                if (!cancelled) {
                    dispatch(
                        setAlert({
                            message: getErrorMessage(error, translate('failedToLoadConsultants')),
                            type: 'error',
                        })
                    );
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        void load();
        return () => {
            cancelled = true;
        };
    }, [dispatch, translate]);

    const filteredConsultants = useMemo(() => {
        const query = search.trim().toLowerCase();

        return consultants
            .filter((consultant) => {
                if (selectedRole && consultant.role !== selectedRole) return false;
                if (
                    selectedProviderId &&
                    !consultant.service_providers.some(
                        (assignment) => assignment.service_provider_id === selectedProviderId
                    )
                ) {
                    return false;
                }
                if (!query) return true;

                const licenseText = consultant.licenses
                    .map(
                        (license) =>
                            `${license.license_number} ${license.country} ${license.date_issued}`
                    )
                    .join(' ');
                return `${getAdminConsultantName(consultant)} ${consultant.email} ${licenseText}`
                    .toLowerCase()
                    .includes(query);
            })
            .sort((left, right) =>
                getAdminConsultantName(left).localeCompare(getAdminConsultantName(right))
            );
    }, [consultants, search, selectedProviderId, selectedRole]);

    const handleCreateConsultant = async (values: CreateConsultantFormValues) => {
        const provider = providers.find((item) => item.id === Number(values.service_provider_id));
        if (!provider) return;

        const payload: CreateConsultantPayload = {
            firstname: values.firstname.trim(),
            middlename: values.middlename.trim() || null,
            lastname: values.lastname.trim(),
            email: values.email.trim().toLowerCase(),
            role: values.role as AdminConsultantRole,
            licenses: values.licenses.map((license) => ({
                country: license.country as Exclude<typeof license.country, ''>,
                license_number: license.license_number.trim(),
                date_issued: license.date_issued,
            })),
        };

        try {
            const response = await api.serviceProvider.createConsultant(provider.id, payload);
            setConsultants((current) => [...current, toAdminConsultant(response.data, provider)]);
            onResetModalObj();
            dispatch(
                setAlert({
                    message: translate('consultantCreated'),
                    type: 'success',
                })
            );
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('failedToCreateConsultant')),
                    type: 'error',
                })
            );
        }
    };

    const handleEditConsultant = async (
        consultant: AdminConsultant,
        values: EditConsultantFormValues
    ) => {
        const serviceProviderId = consultant.service_providers[0]?.service_provider_id;
        if (!serviceProviderId) return;

        const profile = getConsultantProfileUpdate(consultant, values);
        const licenseEdits = getConsultantLicenseEdits(consultant, values);

        try {
            const profileResponse = profile
                ? await api.serviceProvider.updateConsultant(
                      serviceProviderId,
                      consultant.id,
                      profile
                  )
                : null;
            const licenses = await persistConsultantLicenseEdits(
                { serviceProviderId, consultantId: consultant.id },
                consultant.licenses,
                licenseEdits,
                values.licenses
            );

            setConsultants((current) =>
                current.map((item) =>
                    item.id === consultant.id
                        ? {
                              ...item,
                              ...(profileResponse
                                  ? {
                                        firstname: profileResponse.data.firstname,
                                        middlename: profileResponse.data.middlename,
                                        lastname: profileResponse.data.lastname,
                                        email: profileResponse.data.email,
                                        role: profileResponse.data.role,
                                        status: profileResponse.data.status,
                                    }
                                  : {}),
                              licenses,
                          }
                        : item
                )
            );
            onResetModalObj();
            dispatch(
                setAlert({
                    message: translate('consultantUpdated'),
                    type: 'success',
                })
            );
        } catch (error) {
            try {
                const refreshed = await loadAllConsultants();
                setConsultants(
                    refreshed.filter((item) => ADMIN_CONSULTANT_ROLES.includes(item.role))
                );
            } catch {
                // Keep the visible rows when the recovery request also fails.
            }
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('failedToUpdateConsultant')),
                    type: 'error',
                })
            );
        }
    };

    const handleAssignmentUpdated = (
        consultantId: number,
        provider: ServiceProviderAdminRow,
        update: ConsultantServiceProviderChange
    ) => {
        setConsultants((current) =>
            current.map((consultant) => {
                let assignments = consultant.service_providers.map((assignment) =>
                    update.is_default_consultant && assignment.service_provider_id === provider.id
                        ? {
                              ...assignment,
                              is_default_consultant: consultant.id === consultantId,
                          }
                        : assignment
                );

                if (consultant.id !== consultantId) {
                    return { ...consultant, service_providers: assignments };
                }

                assignments = assignments.filter(
                    (assignment) => assignment.service_provider_id !== provider.id
                );
                if (update.assigned) {
                    assignments.push({
                        service_provider_id: provider.id,
                        company_name: provider.company_name,
                        is_default_consultant: update.is_default_consultant,
                    });
                }

                return { ...consultant, service_providers: assignments };
            })
        );
    };

    const editingConsultant =
        modalObject.action === ACTIONS.EDIT && modalObject.data
            ? (consultants.find((consultant) => consultant.id === modalObject.data?.id) ??
              modalObject.data)
            : null;

    return (
        <>
            <Stack spacing={2} sx={{ mb: 2 }}>
                <PageHeader title="adminPanel" titleEnd={headerControls} sx={{ mb: 0 }}>
                    <Button
                        variant="contained"
                        startIcon={<PersonAddOutlined />}
                        disabled={!providers.length}
                        onClick={() =>
                            setModalObject((current) => ({
                                ...current,
                                action: ACTIONS.CREATE,
                            }))
                        }
                    >
                        {translate('addConsultant')}
                    </Button>
                </PageHeader>
                <ConsultantsFilters
                    search={search}
                    onSearchChange={setSearch}
                    selectedRole={selectedRole}
                    onRoleChange={setSelectedRole}
                    providers={providers}
                    selectedProviderId={selectedProviderId}
                    onProviderChange={setSelectedProviderId}
                />
            </Stack>

            <ConsultantsTable
                consultants={filteredConsultants}
                loading={isLoading}
                onEdit={(consultant) =>
                    setModalObject((current) => ({
                        ...current,
                        action: ACTIONS.EDIT,
                        data: consultant,
                    }))
                }
                onManageProviders={(consultant) =>
                    setModalObject((current) => ({
                        ...current,
                        action: 'SERVICE_PROVIDERS',
                        data: consultant,
                    }))
                }
            />

            {modalObject.action === ACTIONS.CREATE ? (
                <CreateConsultantDialog
                    providers={providers}
                    initialProviderId={selectedProviderId}
                    onClose={onResetModalObj}
                    onSubmit={handleCreateConsultant}
                />
            ) : null}

            {editingConsultant ? (
                <EditConsultantDialog
                    consultant={editingConsultant}
                    onClose={onResetModalObj}
                    onSubmit={(values) => handleEditConsultant(editingConsultant, values)}
                />
            ) : null}

            {modalObject.action === 'SERVICE_PROVIDERS' && modalObject.data ? (
                <ConsultantServiceProvidersDialog
                    consultant={
                        consultants.find((consultant) => consultant.id === modalObject.data?.id) ??
                        modalObject.data
                    }
                    providers={providers}
                    onClose={onResetModalObj}
                    onAssignmentUpdated={handleAssignmentUpdated}
                />
            ) : null}
        </>
    );
}
